import { Price } from '../../../models/PricebookData';
import { RevShareEntry } from '../../../models/RevShareData';
import { AdminTierTypeEnum, tierOrder } from '../../../services/drillDown';
import {
  RevShareFormValues,
  RevShareFields,
  RevShareSettingOption,
  deriveRevShareStatus,
} from './revShareSettingsUtils';

export const getRevShareFormInitialValues = (
  pricebookId: number,
  currentSetting: RevShareEntry | null,
  parentSetting: RevShareEntry | null,
  adminTier: AdminTierTypeEnum,
  price: Price | undefined,
): RevShareFormValues => {
  const status = deriveRevShareStatus(currentSetting, price);
  const parentRevShareStatus = deriveRevShareStatus(parentSetting, price);

  const parentRevShareFields: RevShareFields | undefined =
    parentRevShareStatus.type === 'set'
      ? {
          revShareMethod: parentRevShareStatus.method,
          baseAmount: parentRevShareStatus.baseAmount,
          revSharePct: parentRevShareStatus.percentage,
          fixedAmount: parentRevShareStatus.fixedAmount,
          effectiveTimestamp: parentSetting?.effectiveTimestamp,
          expireTimestamp: parentSetting?.expireTimestamp || null,
          setEndDate: parentSetting?.expireTimestamp ? 'Yes' : 'No',
        }
      : undefined;

  const parentTierName: AdminTierTypeEnum | undefined = (() => {
    if (parentSetting?.tier && parentSetting.tier.toLowerCase() !== adminTier.toLowerCase()) {
      return parentSetting.tier as AdminTierTypeEnum;
    }
    const tierIndex = tierOrder.indexOf(adminTier);
    return tierIndex > 0 ? tierOrder[tierIndex - 1] : undefined;
  })();

  const parentHasSetting =
    parentRevShareStatus.type === 'set' || parentRevShareStatus.type === 'disabled';

  const initialSelection: RevShareSettingOption | undefined = (() => {
    if (status.type === 'disabled') return 'No fee share';
    return 'Set fee share';
  })();

  const initialFields: Partial<RevShareFields> = (() => {
    if (status.type === 'set') {
      return {
        revShareMethod: status.method,
        baseAmount: status.baseAmount,
        revSharePct: status.percentage,
        fixedAmount: status.fixedAmount,
      };
    }
    return {};
  })();

  const effectiveTimestamp = currentSetting?.effectiveTimestamp ?? new Date().toISOString();

  const hasExpiration = Boolean(
    currentSetting?.expireTimestamp && currentSetting.expireTimestamp !== '',
  );

  return {
    pricebookId,
    parentTier: parentHasSetting ? parentTierName : undefined,
    revShareSetting: initialSelection,
    effectiveTimestamp,
    expireTimestamp: hasExpiration ? (currentSetting?.expireTimestamp ?? null) : null,
    setEndDate: hasExpiration ? 'Yes' : 'No',
    ...initialFields,
    parentRevShareFields,
    existingEntryId: currentSetting?.id,
  };
};

export const validateRevShareForm = (pricebookPrice: number) => (values: RevShareFormValues) => {
  const errors: Partial<Record<keyof RevShareFormValues, string>> = {};

  const GT_ZERO = 'Amount must be greater than 0';
  const GT_SALE = "Amount can't be greater than sale price";
  const GT_100 = "Amount can't be greater than 100%";

  const setting = values.revShareSetting;

  if (!setting) {
    errors.revShareSetting = 'Required';
    return errors;
  }

  if (!values.effectiveTimestamp) {
    errors.effectiveTimestamp = 'Required';
  }

  if (values.setEndDate === 'Yes' && !values.expireTimestamp) {
    errors.expireTimestamp = 'Required';
  }

  if (values.setEndDate === 'Yes' && values.effectiveTimestamp && values.expireTimestamp) {
    const effectiveDay = new Date(values.effectiveTimestamp);
    effectiveDay.setUTCHours(0, 0, 0, 0);
    const expireDay = new Date(values.expireTimestamp);
    expireDay.setUTCHours(0, 0, 0, 0);
    if (expireDay < effectiveDay) {
      errors.expireTimestamp = 'Expiration must be after effective date';
    }
  }

  if (setting === 'No fee share') return errors;

  const method = values.revShareMethod;

  if (!method) {
    errors.revShareMethod = 'Required';
    return errors;
  }

  const hasPriceCap = pricebookPrice > 0;

  const validateMoney = (field: 'baseAmount' | 'fixedAmount', value: number | undefined) => {
    if (value == null) {
      errors[field] = 'Required';
      return;
    }
    if (value <= 0) {
      errors[field] = GT_ZERO;
      return;
    }
    if (hasPriceCap && value > pricebookPrice) {
      errors[field] = GT_SALE;
    }
  };

  if (method === '% of sale') {
    validateMoney('baseAmount', values.baseAmount);

    const pct = values.revSharePct;
    if (pct == null) {
      errors.revSharePct = 'Required';
    } else if (pct <= 0) {
      errors.revSharePct = GT_ZERO;
    } else if (pct > 100) {
      errors.revSharePct = GT_100;
    }
  } else if (method === 'Fixed $ amt') {
    validateMoney('fixedAmount', values.fixedAmount);
  }

  return errors;
};
